/**
 * Unequal (Futoshiki) Solver
 *
 * Implements constraint propagation and backtracking solver
 * for validating puzzles and determining difficulty.
 */

import {
  GameMode,
  GameState,
  Difficulty,
  DIRECTIONS,
} from './types';

/**
 * Solver context - tracks possibilities for each cell
 */
export interface SolverContext {
  order: number;
  mode: GameMode;
  flags: number[];
  // cube[y][x][n] = true means value n+1 is possible at (x,y)
  cube: boolean[][][];
  // Current known values
  grid: number[];
}

/**
 * Create solver context from game state
 */
export function createSolverContext(state: GameState): SolverContext {
  const { order, mode, flags, nums } = state;

  // Initialize possibility cube - all values possible initially
  const cube: boolean[][][] = [];
  for (let y = 0; y < order; y++) {
    cube[y] = [];
    for (let x = 0; x < order; x++) {
      cube[y]![x] = [];
      const value = nums[y * order + x] ?? 0;
      if (value === 0) {
        // Empty cell - all values possible
        for (let n = 0; n < order; n++) {
          cube[y]![x]![n] = true;
        }
      } else {
        // Filled cell - only this value possible
        for (let n = 0; n < order; n++) {
          cube[y]![x]![n] = n === value - 1;
        }
      }
    }
  }

  return {
    order,
    mode,
    flags: [...flags],
    cube,
    grid: [...nums],
  };
}

/**
 * Count possibilities at a cell
 */
function countPossibilities(ctx: SolverContext, x: number, y: number): number {
  let count = 0;
  for (let n = 0; n < ctx.order; n++) {
    if (ctx.cube[y]![x]![n]) count++;
  }
  return count;
}

/**
 * Get the only possibility at a cell (if unique)
 */
function getOnlyPossibility(
  ctx: SolverContext,
  x: number,
  y: number
): number | null {
  let found: number | null = null;
  for (let n = 0; n < ctx.order; n++) {
    if (ctx.cube[y]![x]![n]) {
      if (found !== null) return null; // Multiple possibilities
      found = n + 1;
    }
  }
  return found;
}

/**
 * Eliminate a possibility at a cell
 */
function eliminate(
  ctx: SolverContext,
  x: number,
  y: number,
  n: number
): boolean {
  if (!ctx.cube[y]![x]![n]) return false;
  ctx.cube[y]![x]![n] = false;
  return true;
}

/**
 * Basic Latin square constraint propagation
 * Eliminate values already used in same row/column
 */
function propagateLatinSquare(ctx: SolverContext): number {
  let changes = 0;
  const { order, grid, cube } = ctx;

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      const value = grid[y * order + x] ?? 0;
      if (value === 0) continue;

      const n = value - 1;

      // Eliminate from row
      for (let xx = 0; xx < order; xx++) {
        if (xx !== x && cube[y]![xx]![n]) {
          cube[y]![xx]![n] = false;
          changes++;
        }
      }

      // Eliminate from column
      for (let yy = 0; yy < order; yy++) {
        if (yy !== y && cube[yy]![x]![n]) {
          cube[yy]![x]![n] = false;
          changes++;
        }
      }
    }
  }

  return changes;
}

/**
 * Solve cells with only one possibility (naked singles)
 */
function solveNakedSingles(ctx: SolverContext): number {
  let changes = 0;
  const { order, grid } = ctx;

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      if ((grid[y * order + x] ?? 0) !== 0) continue;

      const only = getOnlyPossibility(ctx, x, y);
      if (only !== null) {
        grid[y * order + x] = only;
        changes++;
      }
    }
  }

  return changes;
}

/**
 * Check for hidden singles in rows and columns
 */
function solveHiddenSingles(ctx: SolverContext): number {
  let changes = 0;
  const { order, grid, cube } = ctx;

  // Check rows
  for (let y = 0; y < order; y++) {
    for (let n = 0; n < order; n++) {
      let count = 0;
      let lastX = -1;
      for (let x = 0; x < order; x++) {
        if ((grid[y * order + x] ?? 0) === n + 1) {
          count = -1;
          break;
        }
        if (cube[y]![x]![n]) {
          count++;
          lastX = x;
        }
      }
      if (count === 1 && lastX >= 0 && (grid[y * order + lastX] ?? 0) === 0) {
        grid[y * order + lastX] = n + 1;
        // Clear other possibilities
        for (let nn = 0; nn < order; nn++) {
          cube[y]![lastX]![nn] = nn === n;
        }
        changes++;
      }
    }
  }

  // Check columns
  for (let x = 0; x < order; x++) {
    for (let n = 0; n < order; n++) {
      let count = 0;
      let lastY = -1;
      for (let y = 0; y < order; y++) {
        if ((grid[y * order + x] ?? 0) === n + 1) {
          count = -1;
          break;
        }
        if (cube[y]![x]![n]) {
          count++;
          lastY = y;
        }
      }
      if (count === 1 && lastY >= 0 && (grid[lastY * order + x] ?? 0) === 0) {
        grid[lastY * order + x] = n + 1;
        // Clear other possibilities
        for (let nn = 0; nn < order; nn++) {
          cube[lastY]![x]![nn] = nn === n;
        }
        changes++;
      }
    }
  }

  return changes;
}

/**
 * Propagate inequality constraints (UNEQUAL mode)
 */
function propagateUnequal(ctx: SolverContext): number {
  let changes = 0;
  const { order, flags, cube } = ctx;

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      const cellFlags = flags[y * order + x] ?? 0;

      for (const dir of DIRECTIONS) {
        if (!(cellFlags & dir.flag)) continue;

        const nx = x + dir.dx;
        const ny = y + dir.dy;

        if (nx < 0 || nx >= order || ny < 0 || ny >= order) continue;

        // This cell must be > neighbor cell
        // Find min/max possible values
        let thisMin = order + 1,
          thisMax = 0;
        let neighborMin = order + 1,
          neighborMax = 0;

        for (let n = 0; n < order; n++) {
          if (cube[y]![x]![n]) {
            thisMin = Math.min(thisMin, n + 1);
            thisMax = Math.max(thisMax, n + 1);
          }
          if (cube[ny]![nx]![n]) {
            neighborMin = Math.min(neighborMin, n + 1);
            neighborMax = Math.max(neighborMax, n + 1);
          }
        }

        // Eliminate values that can't satisfy this > neighbor
        // This cell must be at least neighborMin + 1
        for (let n = 0; n < neighborMin; n++) {
          if (eliminate(ctx, x, y, n)) changes++;
        }

        // Neighbor must be at most thisMax - 1
        for (let n = thisMax; n <= order; n++) {
          if (eliminate(ctx, nx, ny, n - 1)) changes++;
        }
      }
    }
  }

  return changes;
}

/**
 * Propagate adjacency constraints (ADJACENT mode)
 */
function propagateAdjacent(ctx: SolverContext): number {
  let changes = 0;
  const { order, flags, grid } = ctx;

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      const value = grid[y * order + x] ?? 0;
      if (value === 0) continue;

      const cellFlags = flags[y * order + x] ?? 0;

      for (const dir of DIRECTIONS) {
        const nx = x + dir.dx;
        const ny = y + dir.dy;

        if (nx < 0 || nx >= order || ny < 0 || ny >= order) continue;

        const hasAdjacency = (cellFlags & dir.flag) !== 0;

        // Eliminate impossible values in neighbor
        for (let n = 0; n < order; n++) {
          const neighborValue = n + 1;
          const difference = Math.abs(value - neighborValue);

          // If marked adjacent, only consecutive values allowed
          if (hasAdjacency && difference !== 1) {
            if (eliminate(ctx, nx, ny, n)) changes++;
          }

          // If NOT marked adjacent, consecutive values NOT allowed
          if (!hasAdjacency && difference === 1) {
            if (eliminate(ctx, nx, ny, n)) changes++;
          }
        }
      }
    }
  }

  return changes;
}

/**
 * Advanced set-based reasoning for adjacent mode
 */
function propagateAdjacentSet(ctx: SolverContext): number {
  let changes = 0;
  const { order, flags, cube } = ctx;

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      const cellFlags = flags[y * order + x] ?? 0;

      for (const dir of DIRECTIONS) {
        const nx = x + dir.dx;
        const ny = y + dir.dy;

        if (nx < 0 || nx >= order || ny < 0 || ny >= order) continue;

        const hasAdjacency = (cellFlags & dir.flag) !== 0;

        // Build set of possible values for neighbor
        const possibleSet = new Array<boolean>(order).fill(false);

        for (let n = 0; n < order; n++) {
          if (!cube[y]![x]![n]) continue;

          for (let nn = 0; nn < order; nn++) {
            if (n === nn) continue;

            const difference = Math.abs(nn - n);

            if (hasAdjacency && difference !== 1) continue;
            if (!hasAdjacency && difference === 1) continue;

            possibleSet[nn] = true;
          }
        }

        // Remove any possibilities not in the set
        for (let n = 0; n < order; n++) {
          if (!possibleSet[n] && cube[ny]![nx]![n]) {
            cube[ny]![nx]![n] = false;
            changes++;
          }
        }
      }
    }
  }

  return changes;
}

/**
 * Check if the current state is valid (no contradictions)
 */
function isValid(ctx: SolverContext): boolean {
  const { order, grid, flags, mode } = ctx;

  // Check for cells with no possibilities
  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      if ((grid[y * order + x] ?? 0) === 0 && countPossibilities(ctx, x, y) === 0) {
        return false;
      }
    }
  }

  // Check constraints
  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      const value = grid[y * order + x] ?? 0;
      if (value === 0) continue;

      const cellFlags = flags[y * order + x] ?? 0;

      for (const dir of DIRECTIONS) {
        const nx = x + dir.dx;
        const ny = y + dir.dy;

        if (nx < 0 || nx >= order || ny < 0 || ny >= order) continue;

        const neighborValue = grid[ny * order + nx] ?? 0;
        if (neighborValue === 0) continue;

        const hasConstraint = (cellFlags & dir.flag) !== 0;

        if (mode === GameMode.ADJACENT) {
          const diff = Math.abs(value - neighborValue);
          if (hasConstraint && diff !== 1) return false;
          if (!hasConstraint && diff === 1) return false;
        } else {
          // UNEQUAL: this > neighbor
          if (hasConstraint && value <= neighborValue) return false;
        }
      }
    }
  }

  return true;
}

/**
 * Check if puzzle is complete
 */
function isComplete(ctx: SolverContext): boolean {
  const { order, grid } = ctx;
  for (let i = 0; i < order * order; i++) {
    if ((grid[i] ?? 0) === 0) return false;
  }
  return true;
}

/**
 * Find cell with fewest possibilities for branching
 */
function findBranchingCell(
  ctx: SolverContext
): { x: number; y: number } | null {
  const { order, grid } = ctx;
  let minCount = order + 1;
  let bestX = -1,
    bestY = -1;

  for (let y = 0; y < order; y++) {
    for (let x = 0; x < order; x++) {
      if ((grid[y * order + x] ?? 0) !== 0) continue;

      const count = countPossibilities(ctx, x, y);
      if (count < minCount) {
        minCount = count;
        bestX = x;
        bestY = y;
      }
    }
  }

  return bestX >= 0 ? { x: bestX, y: bestY } : null;
}

/**
 * Clone solver context
 */
function cloneContext(ctx: SolverContext): SolverContext {
  const { order } = ctx;
  const newCube: boolean[][][] = [];

  for (let y = 0; y < order; y++) {
    newCube[y] = [];
    for (let x = 0; x < order; x++) {
      newCube[y]![x] = [...ctx.cube[y]![x]!];
    }
  }

  return {
    order: ctx.order,
    mode: ctx.mode,
    flags: [...ctx.flags],
    cube: newCube,
    grid: [...ctx.grid],
  };
}

/**
 * Main solver function
 *
 * Returns number of solutions found (0, 1, or 2 for "multiple")
 */
export function solve(
  ctx: SolverContext,
  maxDifficulty: Difficulty,
  countLimit: number = 2
): number {
  // Constraint propagation loop
  let changed = true;
  while (changed) {
    changed = false;

    // Basic Latin square propagation (always)
    if (propagateLatinSquare(ctx) > 0) changed = true;
    if (solveNakedSingles(ctx) > 0) changed = true;

    if (!isValid(ctx)) return 0;

    // DIFF_EASY techniques
    if (maxDifficulty >= Difficulty.EASY) {
      if (ctx.mode === GameMode.UNEQUAL) {
        if (propagateUnequal(ctx) > 0) changed = true;
      } else {
        if (propagateAdjacent(ctx) > 0) changed = true;
      }
      if (solveNakedSingles(ctx) > 0) changed = true;
      if (solveHiddenSingles(ctx) > 0) changed = true;
    }

    if (!isValid(ctx)) return 0;

    // DIFF_SET techniques
    if (maxDifficulty >= Difficulty.SET && ctx.mode === GameMode.ADJACENT) {
      if (propagateAdjacentSet(ctx) > 0) changed = true;
      if (solveNakedSingles(ctx) > 0) changed = true;
    }

    if (!isValid(ctx)) return 0;
  }

  // Check if complete
  if (isComplete(ctx)) {
    return isValid(ctx) ? 1 : 0;
  }

  // Need backtracking?
  if (maxDifficulty < Difficulty.RECURSIVE) {
    return 0; // Can't solve without recursion
  }

  // Find cell to branch on
  const branch = findBranchingCell(ctx);
  if (!branch) return 0;

  const { x, y } = branch;
  let solutions = 0;

  for (let n = 0; n < ctx.order && solutions < countLimit; n++) {
    if (!ctx.cube[y]![x]![n]) continue;

    const copy = cloneContext(ctx);
    copy.grid[y * copy.order + x] = n + 1;

    // Clear other possibilities
    for (let nn = 0; nn < copy.order; nn++) {
      copy.cube[y]![x]![nn] = nn === n;
    }

    solutions += solve(copy, maxDifficulty, countLimit - solutions);
  }

  return solutions;
}

/**
 * Solve a game state and return number of solutions
 */
export function solveState(state: GameState, maxDifficulty: Difficulty): number {
  const ctx = createSolverContext(state);
  return solve(ctx, maxDifficulty);
}

/**
 * Get solved grid for a game state
 */
export function getSolution(state: GameState): number[] | null {
  const ctx = createSolverContext(state);
  const numSolutions = solve(ctx, Difficulty.RECURSIVE);

  if (numSolutions === 1) {
    return ctx.grid;
  }

  return null;
}
